/* ============================================================
   Viori Personalizer 2.0 — V6 Shop Quiz Controller
   ------------------------------------------------------------
   Powers the single-page filter drawers that open from
   Main Menu 2.0. Each entry point shows ONE panel only — no
   step-through. Tap Submit to land on /collections/all with
   that single filter applied.

   URL hash conventions (set on menu links in admin):
     #shop-quiz-concern    → Hair Concerns panel
     #shop-quiz-benefit    → Hair Benefits panel
     #shop-quiz-hair-type  → Hair Type panel

   On Submit, the controller builds a /collections/all URL using
   V6 internal params and navigates:
     concern=CSV
     benefit=CSV
     scalp_type=CSV   (Hair Type maps to scalp_type)
   Empty selections → /collections/all (no params).

   Back arrow closes the quiz and reopens the Stiletto drawer
   at the Shop submenu.
   ============================================================ */
(function () {
  'use strict';

  if (window.__VShopQuizBooted) return;
  window.__VShopQuizBooted = true;

  // ---------- constants ----------
  var HASH_TO_STEP = {
    '#shop-quiz-concern': 'concern',
    '#shop-quiz-benefit': 'benefit',
    '#shop-quiz-hair-type': 'hair_type',
    '#shop-quiz-hair_type': 'hair_type'
  };
  var STEP_TO_PARAM = {
    concern: 'concern',
    benefit: 'benefit',
    hair_type: 'scalp_type'
  };
  var COLLECTION_URL = '/collections/all';

  // Grey-out config — same SRA endpoint the V6 quiz + filter bar use.
  // The filter-data section emits collection.filters as JSON so we can
  // dim option values whose product count is zero under the current set.
  var FILTER_DEFS_SECTION = 'cust-v6-filter-data';
  var REFRESH_DEBOUNCE_MS = 250;

  // Shopify's native filter param names (used in SRA URLs). The shop-quiz
  // categories map to these like the big quiz: concern → shop_by_concern,
  // benefit → shop_by_benefit, hair_type → scalp_type.
  var CATEGORY_TO_SHOPIFY_PARAM = {
    concern:    'filter.p.m.custom.shop_by_concern',
    benefit:    'filter.p.m.custom.shop_by_benefit',
    hair_type:  'filter.p.m.custom.scalp_type'
  };
  // Reverse map: Shopify filter.param_name → our category key.
  var SHOPIFY_PARAM_TO_CATEGORY = {
    'filter.p.m.custom.shop_by_concern': 'concern',
    'filter.p.m.custom.shop_by_benefit': 'benefit',
    'filter.p.m.custom.scalp_type':      'hair_type'
  };

  // ---------- state ----------
  var state = {
    root: null,
    overlay: null,
    panels: {},          // { concern: el, benefit: el, hair_type: el }
    currentStep: null,   // 'concern' | 'benefit' | 'hair_type' | null
    startStep: null,     // first opened step for this session
    history: [],         // step history within current open session
    selections: {        // arrays of selected values
      concern: [],
      benefit: [],
      hair_type: []
    },
    bodyOverflow: ''     // saved before lock
  };

  // ---------- helpers ----------
  function $(sel, el) { return (el || document).querySelector(sel); }
  function $all(sel, el) { return Array.prototype.slice.call((el || document).querySelectorAll(sel)); }

  function lockBody() {
    state.bodyOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
  }
  function unlockBody() {
    document.body.style.overflow = state.bodyOverflow || '';
  }

  function findRoot() {
    return document.querySelector('[data-v-shop-quiz]');
  }

  function bindPanels() {
    if (!state.root) return false;
    state.overlay = $('[data-v-shop-quiz-overlay]', state.root);
    state.panels = {};
    $all('[data-v-shop-quiz-panel]', state.root).forEach(function (p) {
      var key = p.getAttribute('data-v-shop-quiz-panel');
      state.panels[key] = p;
    });
    return Object.keys(state.panels).length > 0;
  }

  // Restore checkbox visual state from in-memory selections (so reopening
  // the same panel within a session shows previous picks).
  function syncPanelChecks(stepKey) {
    var panel = state.panels[stepKey];
    if (!panel) return;
    var picks = state.selections[stepKey] || [];
    $all('[data-v-shop-quiz-option]', panel).forEach(function (input) {
      var checked = picks.indexOf(input.value) !== -1;
      input.checked = checked;
      var opt = input.closest('.v-shop-quiz__option');
      if (opt) opt.classList.toggle('is-checked', checked);
    });
  }

  // ---------- show / hide ----------
  function openQuizAt(stepKey) {
    if (!state.root) return;
    if (!state.panels[stepKey]) return;

    state.currentStep = stepKey;
    state.startStep = stepKey;
    state.history = [stepKey];

    state.root.hidden = false;
    state.root.setAttribute('aria-hidden', 'false');
    // force reflow so transition fires
    /* eslint-disable no-unused-expressions */
    state.root.offsetHeight;
    /* eslint-enable no-unused-expressions */
    state.root.classList.add('is-open');

    showPanel(stepKey);
    lockBody();

    // Initial availability fetch on open. With empty selections the API
    // returns full counts and nothing is dimmed — but if a user re-enters
    // the panel mid-session with prior picks, this restores correct dims.
    refreshAvailabilityNow();
  }

  function showPanel(stepKey) {
    Object.keys(state.panels).forEach(function (k) {
      var panel = state.panels[k];
      if (k === stepKey) {
        panel.hidden = false;
        panel.setAttribute('aria-hidden', 'false');
        // reflow so transform transition kicks in
        /* eslint-disable no-unused-expressions */
        panel.offsetHeight;
        /* eslint-enable no-unused-expressions */
        panel.classList.add('is-active');
        panel.classList.remove('is-leaving-back', 'is-leaving-forward');
      } else {
        panel.classList.remove('is-active', 'is-leaving-back', 'is-leaving-forward');
        panel.setAttribute('aria-hidden', 'true');
        panel.hidden = true;
      }
    });
    syncPanelChecks(stepKey);
  }

  function closeQuiz() {
    if (!state.root) return;
    state.root.classList.remove('is-open');
    Object.keys(state.panels).forEach(function (k) {
      var panel = state.panels[k];
      panel.classList.remove('is-active');
      panel.setAttribute('aria-hidden', 'true');
      panel.hidden = true;
    });
    setTimeout(function () {
      if (!state.root.classList.contains('is-open')) {
        state.root.hidden = true;
        state.root.setAttribute('aria-hidden', 'true');
      }
    }, 240);
    state.currentStep = null;
    state.history = [];
    unlockBody();

    // Strip the trigger hash so it doesn't reopen on reload.
    if (window.location.hash && /^#shop-quiz-/.test(window.location.hash)) {
      try {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch (_) { /* noop */ }
    }
  }

  // Reset selections so a closed-and-reopened quiz starts clean.
  function resetSelections() {
    state.selections = { concern: [], benefit: [], hair_type: [] };
    Object.keys(state.panels).forEach(function (k) {
      var panel = state.panels[k];
      $all('[data-v-shop-quiz-option]', panel).forEach(function (input) {
        input.checked = false;
        input.disabled = false;
        var opt = input.closest('.v-shop-quiz__option');
        if (opt) opt.classList.remove('is-checked', 'is-disabled');
      });
    });
  }

  // ---------- selection handling ----------
  function onOptionChange(input) {
    var category = input.getAttribute('data-category');
    var value = input.value;
    if (!category || !value) return;
    var arr = state.selections[category];
    if (!arr) return;
    var idx = arr.indexOf(value);
    if (input.checked && idx === -1) arr.push(value);
    if (!input.checked && idx !== -1) arr.splice(idx, 1);

    var opt = input.closest('.v-shop-quiz__option');
    if (opt) opt.classList.toggle('is-checked', !!input.checked);

    // Grey-out narrows as user picks. Debounced 250ms so rapid taps
    // don't fire one fetch per click.
    scheduleAvailabilityRefresh();
  }

  // ---------- grey-out: SRA-driven option availability ----------
  // Translates state.selections to Shopify's native filter params and
  // GETs /collections/all?{native}&section_id=cust-v6-filter-data.
  // The response contains collection.filters with per-value counts;
  // values with count===0 (and not currently selected) get dimmed.
  function buildSRAQuery() {
    var p = new URLSearchParams();
    Object.keys(state.selections).forEach(function (cat) {
      var picks = state.selections[cat];
      var param = CATEGORY_TO_SHOPIFY_PARAM[cat];
      if (!picks || !picks.length || !param) return;
      picks.forEach(function (v) { p.append(param, v); });
    });
    return p.toString();
  }

  function fetchAvailability() {
    if (!state.root) return Promise.resolve(null);
    var qs = buildSRAQuery();
    var url = COLLECTION_URL + '?' + (qs ? qs + '&' : '') +
              'section_id=' + encodeURIComponent(FILTER_DEFS_SECTION);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('SRA ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('script[data-v6-filter-defs]');
        if (!node) return null;
        try { return JSON.parse(node.textContent); }
        catch (e) { return null; }
      })
      .catch(function () { return null; });
  }

  function applyAvailability(defs) {
    if (!state.root) return;
    if (!defs || !defs.filters) return;

    // Build category → { value → count } map from the SRA response.
    var byCategory = {};
    defs.filters.forEach(function (f) {
      var cat = SHOPIFY_PARAM_TO_CATEGORY[f.param];
      if (!cat) return;
      byCategory[cat] = {};
      (f.values || []).forEach(function (v) {
        // Shopify returns label === value for list-of-values metafields,
        // which is what our markup uses. Key on label so lookup matches.
        byCategory[cat][v.label] = v.count;
      });
    });

    Object.keys(state.panels).forEach(function (cat) {
      var availability = byCategory[cat];
      if (!availability) return;
      var panel = state.panels[cat];
      $all('[data-v-shop-quiz-option]', panel).forEach(function (input) {
        var label = input.closest('.v-shop-quiz__option');
        if (!label) return;
        var val = input.value;
        var count = availability[val];
        var selected = !!input.checked;
        // A selected option stays interactive even if combo currently yields
        // 0 — user needs to be able to un-tick to recover.
        if (!selected && (count === 0 || typeof count === 'undefined')) {
          label.classList.add('is-disabled');
          input.disabled = true;
        } else {
          label.classList.remove('is-disabled');
          input.disabled = false;
        }
      });
    });
  }

  var refreshTimer = null;
  function scheduleAvailabilityRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      fetchAvailability().then(applyAvailability);
    }, REFRESH_DEBOUNCE_MS);
  }

  function refreshAvailabilityNow() {
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
    fetchAvailability().then(applyAvailability);
  }

  // ---------- back navigation ----------
  // Single-page quizzes: back arrow closes the quiz and reopens the
  // Stiletto hamburger drawer at the top-level menu (no auto-slide
  // into Shop submenu — that triggered an empty drawer state).
  function goBack() {
    closeQuiz();
    // Wait for closeQuiz's transition to settle, then reopen drawer.
    setTimeout(openDrawerAtTop, 60);
  }

  function openDrawerAtTop() {
    try {
      // Stiletto's drawer-menu remembers the last submenu depth via
      // data-depth attributes and .visible classes on nested <ul>s.
      // Reset the slider state to depth 0 BEFORE opening so the back
      // button always lands on the first drawer panel (Take Quiz, Shop,
      // Products, Store Locator, Professionals, Book Now, Rewards, About).
      resetDrawerToTop();

      var checkbox = document.getElementById('nav-state');
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } catch (_) { /* noop */ }
  }

  function resetDrawerToTop() {
    var drawer = document.querySelector('[data-drawer-menu]') ||
                 document.querySelector('.drawer-menu');
    if (!drawer) return;

    // Top-level depth markers used by theme.js:navigate() / navigatePrimary()
    var allLinks = drawer.querySelector('[data-all-links]');
    if (allLinks) {
      allLinks.setAttribute('data-depth', '0');
      allLinks.setAttribute('data-in-initial-position', 'true');
    }
    var primary = drawer.querySelector('[data-primary-container="true"]');
    if (primary) {
      primary.setAttribute('data-depth', '0');
    }

    // Hide every nested submenu the user may have slid into.
    var visibleSubs = drawer.querySelectorAll('ul.visible');
    for (var i = 0; i < visibleSubs.length; i++) {
      visibleSubs[i].classList.remove('visible');
      visibleSubs[i].setAttribute('aria-hidden', 'true');
    }

    // Collapse aria-expanded on any parent link that was opened.
    var openedParents = drawer.querySelectorAll('[aria-expanded="true"]');
    for (var j = 0; j < openedParents.length; j++) {
      openedParents[j].setAttribute('aria-expanded', 'false');
    }

    // Clear any inline height the slider set on .drawer-menu__main.
    var main = drawer.querySelector('[data-main]') ||
               drawer.querySelector('.drawer-menu__main');
    if (main) main.style.height = '';
  }

  // ---------- submit ----------
  function submitQuiz() {
    var qs = new URLSearchParams();
    Object.keys(state.selections).forEach(function (k) {
      var picks = state.selections[k];
      if (!picks || !picks.length) return;
      var param = STEP_TO_PARAM[k];
      if (!param) return;
      // V6 internal CSV convention.
      qs.set(param, picks.join(','));
    });
    var url = COLLECTION_URL + (qs.toString() ? '?' + qs.toString() : '');
    // Brief visual ack: keep panel as-is and navigate.
    window.location.href = url;
  }

  // ---------- event delegation ----------
  function isInsideRoot(el) {
    return state.root && state.root.contains(el);
  }

  function onDocClick(e) {
    var target = e.target;
    if (!target) return;

    // Trigger from menu link with hash.
    var trigger = target.closest && target.closest('a[href]');
    if (trigger && !isInsideRoot(trigger)) {
      var href = trigger.getAttribute('href') || '';
      var hashMatch = href.match(/#shop-quiz-(?:concern|benefit|hair[-_]type)/);
      if (hashMatch) {
        e.preventDefault();
        var key = HASH_TO_STEP[hashMatch[0]];
        if (key) {
          // Reset on fresh open so prior selections don't carry over.
          resetSelections();
          openQuizAt(key);
          // Best-effort: close the Stiletto drawer-menu so the quiz
          // panel isn't sitting under the existing drawer overlay.
          closeNativeDrawer();
        }
        return;
      }

      // Belt-and-suspenders: also intercept parent nav links by their text
      // content, so the drawer opens even if the menu URLs haven't been
      // updated to use #shop-quiz-* hashes. Only matches links whose href
      // is the bare /collections/all (no filter params), so sub-items like
      // "Damaged" still navigate normally to their filter URL.
      // Uses endsWith so both legacy ("Hair Concerns") and current
      // ("Shop by Hair Concerns") menu titles route to the same quiz step.
      var pathOnly = href.split('?')[0].split('#')[0];
      if (/\/collections\/all\/?$/.test(pathOnly)) {
        var label = (trigger.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
        var labelMap = [
          { suffix: 'hair concerns', step: 'concern' },
          { suffix: 'hair benefits', step: 'benefit' },
          { suffix: 'hair type',     step: 'hair_type' }
        ];
        for (var li = 0; li < labelMap.length; li++) {
          var entry = labelMap[li];
          if (label === entry.suffix || label.endsWith(' ' + entry.suffix)) {
            e.preventDefault();
            resetSelections();
            openQuizAt(entry.step);
            closeNativeDrawer();
            return;
          }
        }
      }
    }

    if (!state.root) return;

    // Overlay click → close.
    if (target === state.overlay) {
      closeQuiz();
      return;
    }

    // Buttons inside the quiz.
    if (isInsideRoot(target)) {
      var closeBtn = target.closest('[data-v-shop-quiz-close]');
      if (closeBtn) { e.preventDefault(); closeQuiz(); return; }

      var backBtn = target.closest('[data-v-shop-quiz-back]');
      if (backBtn) { e.preventDefault(); goBack(); return; }

      var submitBtn = target.closest('[data-v-shop-quiz-submit]');
      if (submitBtn) { e.preventDefault(); submitQuiz(); return; }
    }
  }

  function onDocChange(e) {
    var t = e.target;
    if (!t) return;
    if (t.matches && t.matches('[data-v-shop-quiz-option]')) {
      onOptionChange(t);
    }
  }

  function onKeydown(e) {
    if (e.key !== 'Escape') return;
    if (state.root && state.root.classList.contains('is-open')) {
      closeQuiz();
    }
  }

  // Stiletto's drawer-menu uses a checkbox toggle (#nav-state) and a
  // body class. Best-effort close so the new quiz panel takes focus.
  function closeNativeDrawer() {
    try {
      var checkbox = document.getElementById('nav-state');
      if (checkbox && checkbox.checked) {
        checkbox.checked = false;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
      document.body.classList.remove('drawer-menu--open', 'js-drawer-open');
    } catch (_) { /* noop */ }
  }

  // ---------- hash handling on page load ----------
  function maybeOpenFromHash() {
    var h = window.location.hash;
    if (!h) return;
    var step = HASH_TO_STEP[h];
    if (!step) return;
    resetSelections();
    openQuizAt(step);
  }

  function onHashChange() {
    var h = window.location.hash;
    if (!h) return;
    var step = HASH_TO_STEP[h];
    if (!step) return;
    if (state.root && state.root.classList.contains('is-open')) return;
    resetSelections();
    openQuizAt(step);
  }

  // ---------- boot ----------
  function boot() {
    state.root = findRoot();
    if (!state.root) return false;
    if (!bindPanels()) return false;

    document.addEventListener('click', onDocClick, true);
    document.addEventListener('change', onDocChange, true);
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('hashchange', onHashChange);
    return true;
  }

  function init() {
    if (boot()) {
      maybeOpenFromHash();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme-editor section reload safety: rebind if the section markup is
  // re-rendered when settings change.
  document.addEventListener('shopify:section:load', function (evt) {
    if (!evt || !evt.target) return;
    if (evt.target.querySelector && evt.target.querySelector('[data-v-shop-quiz]')) {
      state.root = findRoot();
      bindPanels();
    }
  });
})();
